import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EmptyData from "components/EmptyData";
import { formatDate } from "Utils/helper";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/newsocketService";

const WITHDRAWALS_PAGE_SIZE = 500;

const normalizeText = (value) => String(value ?? "").trim();

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    return {};
  }
};

const normalizeStatus = (value) => {
  const raw = normalizeText(value).toLowerCase();

  if (!raw || raw === "pending" || raw === "submitted" || raw === "new") {
    return "pending";
  }

  if (raw === "under_review" || raw === "under review" || raw === "reviewing" || raw === "in review") {
    return "under_review";
  }

  if (raw === "approved" || raw === "approve") {
    return "approved";
  }

  if (raw === "processed" || raw === "processing") {
    return "processed";
  }

  if (raw === "completed" || raw === "complete" || raw === "paid") {
    return "completed";
  }

  if (raw === "rejected" || raw === "dismissed" || raw === "declined") {
    return "rejected";
  }

  return raw;
};

const normalizeMethod = (value) => {
  const raw = normalizeText(value).toLowerCase();

  if (raw === "zebra" || raw === "zebra wallet") {
    return "zebra";
  }

  if (raw === "paystack") {
    return "paystack";
  }

  return raw;
};

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Under Review", value: "under_review" },
  { label: "Approved", value: "approved" },
  { label: "Processed", value: "processed" },
  { label: "Completed", value: "completed" },
  { label: "Rejected", value: "rejected" },
];

const normalizeTimestamp = (value) => {
  if (!value) {
    return 0;
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric < 1e12 ? numeric * 1000 : numeric;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return `₦${numeric.toLocaleString()}`;
  }

  return String(value);
};

const getStatusMeta = (status) => {
  const normalized = normalizeStatus(status);

  if (normalized === "approved") {
    return { label: "APPROVED", bg: "#E8F0FE", fg: "#1A56DB" };
  }

  if (normalized === "under_review") {
    return { label: "UNDER REVIEW", bg: "#F3E8FF", fg: "#7C3AED" };
  }

  if (normalized === "processed") {
    return { label: "PROCESSED", bg: "#EAF7EF", fg: "#16803C" };
  }

  if (normalized === "completed") {
    return { label: "COMPLETED", bg: "#DCFCE7", fg: "#0F7A3C" };
  }

  if (normalized === "rejected") {
    return { label: "REJECTED", bg: "#FDECEC", fg: "#C62828" };
  }

  return { label: "PENDING", bg: "#FFF5E5", fg: "#A96C00" };
};

const getMethodMeta = (method) => {
  const normalized = normalizeMethod(method);

  if (normalized === "zebra") {
    return { label: "ZEBRA WALLET", bg: "#ECFDF5", fg: "#047857" };
  }

  if (normalized === "paystack") {
    return { label: "PAYSTACK", bg: "#EEF2FF", fg: "#4338CA" };
  }

  return { label: normalizeText(method) || "UNKNOWN", bg: "#F3F4F6", fg: "#4B5563" };
};

const normalizeWithdrawalRecord = (request) => {
  const requestId =
    request?.requestId ||
    request?.reference ||
    request?.id ||
    request?._id?.toString?.() ||
    request?._id ||
    "";
  const submittedAt = request?.submittedAt || request?.createdAt || request?.updatedAt || null;

  return {
    ...request,
    id: request?.id || request?._id?.toString?.() || request?._id || requestId,
    requestId,
    status: normalizeStatus(request?.status),
    withdrawalMethod: normalizeMethod(request?.withdrawalMethod),
    submittedAt,
    reviewedAt:
      request?.reviewedAt ||
      request?.approvedAt ||
      request?.processedAt ||
      request?.completedAt ||
      request?.rejectedAt ||
      null,
    sortValue: normalizeTimestamp(submittedAt || request?.createdAt || request?.updatedAt),
    searchIndex: [
      requestId,
      request?.reference,
      request?.userName,
      request?.fullName,
      request?.phoneNumber,
      request?.userPhone,
      request?.userEmail,
      request?.state,
      request?.withdrawalMethod,
      request?.bankName,
      request?.accountName,
      request?.accountNumber,
      request?.emailAddress,
      request?.identificationType,
      request?.identificationNumber,
      request?.reportReference,
      request?.adminNote,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  };
};

const DetailRow = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <MDTypography variant="caption" color="text.secondary" display="block" fontWeight="bold">
      {label}
    </MDTypography>
    <MDTypography
      variant="body2"
      sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap", color: "#111827" }}
    >
      {value || "--"}
    </MDTypography>
  </Grid>
);

function WhistleBlowerWithdrawals() {
  const navigate = useNavigate();
  const getUserState = useSelector((state) => state?.data?.getUserState);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTxt, setSearchText] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRequest, setMenuRequest] = useState(null);
  const [updatingRequestId, setUpdatingRequestId] = useState(null);

  const userContext = useMemo(() => {
    const storedUser = readStoredUser();
    const isSuperAdmin = Boolean(storedUser?.superAdmin);
    const selectedState = normalizeText(getUserState);

    return {
      storedUser,
      isSuperAdmin,
      scopeLabel: isSuperAdmin
        ? selectedState && selectedState !== "All State"
          ? selectedState.toUpperCase()
          : "ALL STATE"
        : "ALL STATE",
    };
  }, [getUserState]);

  const loadWithdrawals = useCallback(async () => {
    const storedUser = readStoredUser();
    const userId = storedUser?._id || storedUser?.id || storedUser?.userId || localStorage.getItem("userId");

    if (!userId) {
      navigate("/authentication/sign-in");
      return;
    }

    setLoading(true);

    try {
      const requests = [];
      let page = 1;
      let totalPages = 1;

      do {
        const params = {
          page,
          limit: WITHDRAWALS_PAGE_SIZE,
        };

        const response = await apiClient.get("/whistle-blower/withdrawals", { params });
        const pageRecords = Array.isArray(response?.data) ? response.data : [];
        const pagination = response?.pagination || {};

        requests.push(...pageRecords);
        totalPages = Number(pagination?.totalPages || 1);
        page += 1;
      } while (page <= totalPages);

      setRecords(requests.map(normalizeWithdrawalRecord));
    } catch (error) {
      console.error("[WhistleBlowerWithdrawals] failed to fetch requests", error);
      toast.error(error?.message || "Failed to load whistle blower withdrawals");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void loadWithdrawals();
  }, [loadWithdrawals, getUserState]);

  useEffect(() => {
    const refreshFromSocket = (payload) => {
      console.log("[WhistleBlowerWithdrawals] socket payload received", payload);
      const normalizedRequest = payload ? normalizeWithdrawalRecord(payload) : null;
      const isNewRequest = payload?.eventType === "created";

      if (normalizedRequest?.requestId || normalizedRequest?.id) {
        console.log("[WhistleBlowerWithdrawals] normalized withdrawal request", normalizedRequest);
        setRecords((currentRecords) => {
          const nextRecords = currentRecords.filter(
            (record) =>
              record?.requestId !== normalizedRequest.requestId &&
              record?.id !== normalizedRequest.id,
          );

          return [normalizedRequest, ...nextRecords];
        });
        setSelectedRequest((current) =>
          current?.requestId === normalizedRequest.requestId || current?.id === normalizedRequest.id
            ? normalizedRequest
            : current,
        );

        if (isNewRequest) {
          console.log("[WhistleBlowerWithdrawals] opening details modal for new withdrawal request");
          setSelectedRequest(normalizedRequest);
        }
      } else {
        console.log("[WhistleBlowerWithdrawals] payload missing id, reloading list");
        void loadWithdrawals();
      }
    };

    socketService.on("whistleblowerwithdrawal", refreshFromSocket);
    socketService.on("whistleblowerwithdrawalrequest", refreshFromSocket);

    return () => {
      socketService.off("whistleblowerwithdrawal", refreshFromSocket);
      socketService.off("whistleblowerwithdrawalrequest", refreshFromSocket);
    };
  }, [loadWithdrawals]);

  const openStatusMenu = useCallback((event, request) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRequest(request);
  }, []);

  const closeStatusMenu = useCallback(() => {
    if (updatingRequestId) {
      return;
    }
    setMenuAnchorEl(null);
    setMenuRequest(null);
  }, [updatingRequestId]);

  const handleStatusUpdate = useCallback(
    async (request, nextStatus) => {
      if (!request) {
        return;
      }

      const currentStatus = normalizeStatus(request?.status);
      const normalizedNextStatus = normalizeStatus(nextStatus);
      const requestId = request?.id || request?._id;

      if (!requestId) {
        toast.error("Request ID is missing");
        return;
      }

      if (!normalizedNextStatus) {
        toast.error("Please select a valid status");
        return;
      }

      if (normalizedNextStatus === currentStatus) {
        toast.info("Status is already set to this value");
        return;
      }

      setUpdatingRequestId(requestId);

      try {
        await apiClient.patch(`/whistle-blower/withdrawals/${requestId}/status`, {
          status: normalizedNextStatus,
        });
        toast.success("Withdrawal status updated successfully");
        await loadWithdrawals();
        setSelectedRequest((current) =>
          current?.id === requestId || current?._id === requestId ? null : current,
        );
      } catch (error) {
        console.error("[WhistleBlowerWithdrawals] failed to update status", error);
        toast.error(error?.response?.data?.message || error?.message || "Failed to update status");
      } finally {
        setUpdatingRequestId(null);
        setMenuAnchorEl(null);
        setMenuRequest(null);
      }
    },
    [loadWithdrawals],
  );

  const filteredRecords = useMemo(() => {
    const term = normalizeText(searchTxt).toLowerCase();

    return records
      .filter((record) => {
        if (!term) {
          return true;
        }

        return record?.searchIndex?.includes(term);
      })
      .sort((a, b) => (b?.sortValue || 0) - (a?.sortValue || 0));
  }, [records, searchTxt]);

  const rows = useMemo(
    () =>
      filteredRecords.map((record, index) => {
        const statusMeta = getStatusMeta(record?.status);
        const methodMeta = getMethodMeta(record?.withdrawalMethod);
        const destination =
          record?.withdrawalMethod === "zebra"
            ? [record?.emailAddress, record?.identificationType, record?.identificationNumber]
                .filter(Boolean)
                .join(" | ") || "--"
            : [record?.bankName, record?.accountName, record?.accountNumber]
                .filter(Boolean)
                .join(" | ") || "--";

        return {
          sr: index + 1,
          reference: record?.reference || record?.requestId || "--",
          user: (
            <Stack spacing={0.2}>
              <MDTypography variant="body2" fontWeight="bold">
                {record?.fullName || record?.userName || "Anonymous"}
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                {record?.phoneNumber || record?.userPhone || "--"}
              </MDTypography>
            </Stack>
          ),
          submittedAt: formatDate(record?.submittedAt || record?.createdAt || record?.updatedAt),
          method: (
            <Chip
              label={methodMeta.label}
              size="small"
              sx={{
                backgroundColor: methodMeta.bg,
                color: methodMeta.fg,
                fontWeight: 700,
                letterSpacing: "0.03em",
              }}
            />
          ),
          status: (
            <Stack spacing={0.4} alignItems="flex-start">
              <Chip
                label={statusMeta.label}
                size="small"
                sx={{
                  backgroundColor: statusMeta.bg,
                  color: statusMeta.fg,
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                }}
              />
              <MDTypography variant="caption" color="text.secondary">
                {record?.reviewedAt ? formatDate(record?.reviewedAt) : "Not reviewed yet"}
              </MDTypography>
            </Stack>
          ),
          destination: (
            <MDTypography variant="body2" sx={{ wordBreak: "break-word" }}>
              {destination}
            </MDTypography>
          ),
          action: (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <Button
                variant="contained"
                size="small"
                onClick={() => setSelectedRequest(record)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  backgroundColor: "#111827",
                  color: "#fff",
                  boxShadow: "none",
                  borderRadius: 999,
                  px: 2,
                  "&:hover": {
                    backgroundColor: "#000000",
                    boxShadow: "none",
                  },
                }}
              >
                View
              </Button>

              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  onClick={(event) => openStatusMenu(event, record)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
        };
      }),
    [filteredRecords, openStatusMenu],
  );

  const columns = useMemo(
    () => [
      { Header: <span style={{ color: "blue" }}>SR</span>, accessor: "sr", align: "left" },
      {
        Header: <span style={{ color: "blue" }}>REPORT ID</span>,
        accessor: "reference",
        align: "left",
      },
      {
        Header: <span style={{ color: "blue" }}>FULL NAME</span>,
        accessor: "user",
        align: "left",
      },
      {
        Header: <span style={{ color: "blue" }}>SUBMITTED</span>,
        accessor: "submittedAt",
        align: "center",
      },
      {
        Header: <span style={{ color: "blue" }}>METHOD</span>,
        accessor: "method",
        align: "center",
      },
      {
        Header: <span style={{ color: "blue" }}>WITHDRAWAL STATUS</span>,
        accessor: "status",
        align: "center",
      },
      { Header: <span style={{ color: "blue" }}>ACTION</span>, accessor: "action", align: "center" },
    ],
    [],
  );

  const detailItems = useMemo(
    () =>
      selectedRequest
        ? [
            ["Reference", selectedRequest?.reference || selectedRequest?.requestId],
            ["Request ID", selectedRequest?.requestId || selectedRequest?.id],
            ["User Name", selectedRequest?.fullName || selectedRequest?.userName],
            ["Full Name", selectedRequest?.fullName],
            ["Phone Number", selectedRequest?.phoneNumber || selectedRequest?.userPhone],
            ["Email Address", selectedRequest?.emailAddress || selectedRequest?.userEmail],
            ["State", selectedRequest?.state],
            ["Withdrawal Method", getMethodMeta(selectedRequest?.withdrawalMethod).label],
            ["Status", getStatusMeta(selectedRequest?.status).label],
            ["Report Reference", selectedRequest?.reportReference],
            ["Report ID", selectedRequest?.reportId],
            ["Identification Type", selectedRequest?.identificationType],
            ["Identification Number", selectedRequest?.identificationNumber],
            ["Bank Name", selectedRequest?.bankName],
            ["Account Name", selectedRequest?.accountName],
            ["Account Number", selectedRequest?.accountNumber],
            ["Reward Amount", formatAmount(selectedRequest?.rewardAmount)],
            ["Locked Reward", formatAmount(selectedRequest?.lockedReward)],
            ["Admin Note", selectedRequest?.adminNote],
            ["Submitted At", formatDate(selectedRequest?.submittedAt || selectedRequest?.createdAt)],
            ["Reviewed At", formatDate(selectedRequest?.reviewedAt)],
            ["Approved At", formatDate(selectedRequest?.approvedAt)],
            ["Processed At", formatDate(selectedRequest?.processedAt)],
            ["Completed At", formatDate(selectedRequest?.completedAt)],
            ["Rejected At", formatDate(selectedRequest?.rejectedAt)],
          ]
        : [],
    [selectedRequest],
  );

  const menuStatusMeta = useMemo(() => getStatusMeta(menuRequest?.status), [menuRequest]);

  return (
    <DashboardLayout>
      <DashboardNavbar value={searchTxt} onChange={(event) => setSearchText(event.target.value)} />

      <MDBox pt={7} pb={3}>
        <ToastContainer />

        <Card
          sx={{
            minHeight: "60vh",
            overflow: "visible",
          }}
        >
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            sx={{
              textAlign: "center",
            }}
          >
            <MDTypography variant="h6" color="white">
              Whistle Blower Withdrawals
            </MDTypography>
            <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
              Showing all withdrawal requests for {userContext.scopeLabel}
            </MDTypography>
          </MDBox>

          <MDBox pt={3}>
            {loading ? (
              <Box sx={{ py: 8, textAlign: "center" }}>Loading...</Box>
            ) : rows.length > 0 ? (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            ) : (
              <EmptyData />
            )}
          </MDBox>
        </Card>
      </MDBox>

      <Dialog
        open={Boolean(selectedRequest)}
        onClose={() => setSelectedRequest(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Withdrawal Details
          <MDTypography variant="caption" display="block" color="text.secondary">
            {selectedRequest?.reference || selectedRequest?.requestId || "--"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {detailItems.map(([label, value]) => (
              <DetailRow key={label} label={label} value={value} />
            ))}
          </Grid>
        </DialogContent>
      <DialogActions>
          <Button onClick={() => setSelectedRequest(null)} variant="contained" color="info">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeStatusMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 180,
            borderRadius: 1.5,
            mt: 0.75,
            py: 0.5,
            boxShadow: "0 14px 28px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <MenuItem disabled dense sx={{ opacity: 1, minHeight: 34, py: 0.25 }}>
          <ListItemText
            primary={
              <MDTypography variant="caption" fontWeight="bold" color="text.secondary">
                CURRENT STATUS
              </MDTypography>
            }
            secondary={
              <Chip
                label={menuStatusMeta.label}
                size="small"
                sx={{
                  mt: 0.5,
                  height: 22,
                  fontSize: "0.68rem",
                  backgroundColor: menuStatusMeta.bg,
                  color: menuStatusMeta.fg,
                  fontWeight: 700,
                }}
              />
            }
          />
        </MenuItem>

        <Divider />

        <MenuItem disabled dense sx={{ opacity: 1, minHeight: 30, py: 0.25 }}>
          <ListItemText
            primary={
              <MDTypography variant="caption" fontWeight="bold" color="text.secondary">
                CHANGE STATUS
              </MDTypography>
            }
          />
        </MenuItem>

        {STATUS_OPTIONS.map((option) => {
          const isActive = normalizeStatus(menuRequest?.status) === option.value;
          const isUpdating = updatingRequestId && menuRequest?.id === updatingRequestId;

          return (
            <MenuItem
              key={option.value}
              dense
              onClick={() => handleStatusUpdate(menuRequest, option.value)}
              disabled={Boolean(isUpdating)}
              selected={isActive}
              sx={{ minHeight: 34, py: 0.5 }}
            >
              <ListItemText
                primary={option.label}
                secondary={isActive ? "Current status" : undefined}
                primaryTypographyProps={{ variant: "body2" }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
              {isActive ? (
                <ListItemIcon sx={{ minWidth: 0, ml: 1 }}>
                  <Chip
                    label="Active"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      backgroundColor: "#ECFDF5",
                      color: "#047857",
                      fontWeight: 700,
                    }}
                  />
                </ListItemIcon>
              ) : null}
            </MenuItem>
          );
        })}
      </Menu>
    </DashboardLayout>
  );
}

export default WhistleBlowerWithdrawals;
